import { useLocalizedNavigate } from '../../../hooks/useLocalizedNavigate';
import { IEvent, TeamUser, UserRating } from '../../../utils/types';
import UserAvatar from '../../common/UserAvatar/UserAvatar';
import { useAppSelector } from '../../../store/hooks';
import { useState } from 'react';
import ReportUserDialog from './ReportUserDialog/ReportUserDialog';
import { Icon, NumericInput, TextField, Title } from '../../common';
import { COLOURS } from '../../../utils/consts';
import { upsert } from '../../../utils/helpers';

interface EventDetailsTeamsProps {
    eventDetails: IEvent;
    ratings: UserRating[];
    winner: string | null;
    onMarkAsWinner: (teamId: string) => void;
    foundUserInTeam?: TeamUser;
    onUserReport: () => void;
    onRatingChange: (ratings: UserRating[]) => void;
}

const VoteEventTeams = ({
    eventDetails,
    ratings,
    winner,
    onMarkAsWinner,
    onUserReport,
    onRatingChange,
}: EventDetailsTeamsProps) => {
    const navigate = useLocalizedNavigate();
    const auth = useAppSelector(state => state.auth);

    const isOwner = eventDetails.owner.id === auth.authData?.id;

    const [hoveredTrophy, setHoveredTrophy] = useState<string | null>(null);
    const [showReportsDialog, setShowReportsDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<TeamUser | null>(null);

    const userClickHandler = (userId: string) => {
        navigate(`/users/${userId}`);
    };

    const onTrophyHover = (teamId: string) => {
        setHoveredTrophy(teamId);
    };

    const onMouseLeave = () => {
        setHoveredTrophy(null);
    };

    const calculateReportIconColor = (userId: string) => {
        if (eventDetails.reports.includes(userId)) {
            return COLOURS.RED;
        }
        return COLOURS.gray;
    };

    const onReportClick = (user: TeamUser) => {
        setSelectedUser(user);
        setShowReportsDialog(true);
    };

    const confirmReportHandler = () => {
        onUserReport();
    };

    function setRating(rating: number, userId: string) {
        const formattedRating = isNaN(rating) ? null : rating;
        const newRatings = upsert(
            ratings,
            { userId, rating: formattedRating },
            'userId'
        );

        onRatingChange(newRatings);
    }

    return (
        <div className="event-page-teams">
            {eventDetails.teams.map((team, index) => (
                <div key={team.name} className="event-details-team">
                    <div className="event-page-team-header">
                        <Title
                            text={`Team ${index === 0 ? 'A' : 'B'}`}
                            size="l"
                        />
                        {isOwner && (
                            <>
                                {winner === team.name ? (
                                    <Icon
                                        name="bxs-trophy"
                                        cursor="pointer"
                                        size="50px"
                                        color="gold"
                                        title="Winner"
                                    />
                                ) : (
                                    <Icon
                                        name="bx-trophy"
                                        color={
                                            hoveredTrophy === team.name
                                                ? 'gold'
                                                : '#dedede'
                                        }
                                        onMouseEnter={() =>
                                            onTrophyHover(team.name)
                                        }
                                        onMouseLeave={onMouseLeave}
                                        onClick={() =>
                                            onMarkAsWinner(team.name)
                                        }
                                        cursor="pointer"
                                        size="50px"
                                        title="Winner"
                                    />
                                )}
                            </>
                        )}
                    </div>
                    <>
                        {team.users.map(user => {
                            const isUserReported =
                                eventDetails.reports.includes(user.id);

                            return (
                                <div
                                    key={user.id}
                                    className="event-team-user-wrapper vote"
                                >
                                    <div className="event-team-user">
                                        <UserAvatar
                                            imageSrc={user.avatarUrl}
                                            size="m"
                                            onClick={() =>
                                                userClickHandler(user.id)
                                            }
                                            averageRating={user.averageRating}
                                        />
                                        <TextField weight="bold">
                                            {user.username}
                                        </TextField>
                                    </div>
                                    <div className="vote-event-stats">
                                        <div className="vote-event-icons">
                                            {user.id !== auth.authData?.id && (
                                                <>
                                                    {!isUserReported ? (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 8,
                                                            }}
                                                        >
                                                            <NumericInput
                                                                value={
                                                                    ratings.find(
                                                                        rating =>
                                                                            rating.userId ===
                                                                            user.id
                                                                    )?.rating
                                                                }
                                                                onChange={value =>
                                                                    setRating(
                                                                        value,
                                                                        user.id
                                                                    )
                                                                }
                                                            />
                                                            {isOwner &&
                                                                !isUserReported && (
                                                                    <div
                                                                        style={{
                                                                            marginLeft:
                                                                                '10px',
                                                                        }}
                                                                    >
                                                                        <Icon
                                                                            cursor={
                                                                                isUserReported
                                                                                    ? 'default'
                                                                                    : 'pointer'
                                                                            }
                                                                            title={
                                                                                isUserReported
                                                                                    ? 'User reported'
                                                                                    : 'Report user'
                                                                            }
                                                                            color={calculateReportIconColor(
                                                                                user.id
                                                                            )}
                                                                            onClick={() =>
                                                                                isUserReported
                                                                                    ? () => {}
                                                                                    : onReportClick(
                                                                                          user
                                                                                      )
                                                                            }
                                                                            name="bx-error"
                                                                        />
                                                                    </div>
                                                                )}
                                                        </div>
                                                    ) : (
                                                        <Icon
                                                            cursor="default"
                                                            title="User reported"
                                                            color="#eb4034"
                                                            name="bx-error"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                </div>
            ))}
            <ReportUserDialog
                user={selectedUser!}
                eventId={eventDetails.id}
                isOpen={showReportsDialog}
                bodyText={`Are you sure you want to report ${selectedUser?.username} for not showing up to the event?`}
                onClose={() => setShowReportsDialog(false)}
                onReportUser={confirmReportHandler}
            />
        </div>
    );
};

export default VoteEventTeams;
